import { useCallback, useEffect, useRef } from 'react';

import {
  fetchMyPlaylists,
  fetchMyRecordings,
  fetchMyWorks,
  fetchMyAlbums,
  fetchMyArtists,
  ALBUMS_REFRESH_TTL_MS,
  ARTISTS_REFRESH_TTL_MS,
  PLAYLISTS_REFRESH_TTL_MS,
  RECORDINGS_REFRESH_TTL_MS,
  WORKS_REFRESH_TTL_MS,
  DISABLE_LEGACY_LIBRARY_FETCHES,
} from '../store/slices/yourLibrary';
import { useAppDispatch, useAppSelector } from '../store/store';
import { backendClient } from '../lib/api/backendClient';
import { isAppReady, whenAppReady } from '../lib/appReadiness';

const REFRESH_POLL_INTERVAL_MS = 10 * 60 * 1000;
const BACKEND_WARMUP_INTERVAL_MS = 2 * 60 * 1000;
const BACKEND_WARMUP_TIMEOUT_MS = 4_000;

type RefreshKey = 'recordings' | 'works' | 'playlists' | 'albums' | 'artists';

/**
 * Periodically refreshes heavy library datasets in the background so navigation stays warm.
 */
export const useLibraryBackgroundRefresh = (enabled: boolean = true): void => {
  const dispatch = useAppDispatch();
  const userId = useAppSelector((state) => state.auth.user?.id ?? null);
  const disableLegacyFetches = DISABLE_LEGACY_LIBRARY_FETCHES;
  const lastFetchedAt = useAppSelector((state) => state.yourLibrary.lastFetchedAt);
  const recordingsFetchInFlight = useAppSelector((state) => state.yourLibrary.recordingsFetchInFlight);
  const worksFetchInFlight = useAppSelector((state) => state.yourLibrary.worksFetchInFlight);
  const playlistsFetchInFlight = useAppSelector((state) => state.yourLibrary.playlistsFetchInFlight);
  const albumsFetchInFlight = useAppSelector((state) => state.yourLibrary.albumsFetchInFlight);
  const artistsFetchInFlight = useAppSelector((state) => state.yourLibrary.artistsFetchInFlight);
  const recordingsStatus = useAppSelector((state) => state.yourLibrary.recordingsStatus);
  const worksStatus = useAppSelector((state) => state.yourLibrary.worksStatus);
  const playlistsStatus = useAppSelector((state) => state.yourLibrary.playlistsStatus);
  const albumsStatus = useAppSelector((state) => state.yourLibrary.albumsStatus);
  const albumsSyncStatus = useAppSelector((state) => state.yourLibrary.albumsSyncStatus);
  const artistsStatus = useAppSelector((state) => state.yourLibrary.artistsStatus);

  const warmupState = useRef<{ last: number; inFlight: boolean }>({
    last: 0,
    inFlight: false,
  });

  const warmBackend = useCallback(() => {
    if (warmupState.current.inFlight) return;
    const now = Date.now();
    if (now - warmupState.current.last < BACKEND_WARMUP_INTERVAL_MS) return;
    warmupState.current.inFlight = true;
    backendClient
      .get('/status/health', { timeout: BACKEND_WARMUP_TIMEOUT_MS })
      .catch(() => {
        // Ignore warmup failures; regular refresh logic will retry on next interval.
      })
      .finally(() => {
        warmupState.current.inFlight = false;
        warmupState.current.last = Date.now();
      });
  }, []);

  const localInFlight = useRef<Record<RefreshKey, boolean>>({
    recordings: false,
    works: false,
    playlists: false,
    albums: false,
    artists: false,
  });

  const tryRefresh = useCallback(
    (key: RefreshKey) => {
      if (!userId) return;
      const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
      if (!isOnline) return;

      const now = Date.now();

      if (key === 'recordings') {
        if (recordingsFetchInFlight || localInFlight.current.recordings || recordingsStatus === 'loading') return;
        const last = lastFetchedAt.recordings;
        const stale = typeof last !== 'number' || !Number.isFinite(last) || now - last > RECORDINGS_REFRESH_TTL_MS;
        if (!stale) return;
        if (!isAppReady()) {
          void whenAppReady().then(() => tryRefresh('recordings'));
          return;
        }
        localInFlight.current.recordings = true;
        dispatch(fetchMyRecordings())
          .finally(() => {
            localInFlight.current.recordings = false;
          })
          .catch(() => {});
        return;
      }

      if (key === 'works') {
        if (worksFetchInFlight || localInFlight.current.works || worksStatus === 'loading') return;
        const last = lastFetchedAt.works;
        const stale = typeof last !== 'number' || !Number.isFinite(last) || now - last > WORKS_REFRESH_TTL_MS;
        if (!stale) return;
        if (!isAppReady()) {
          void whenAppReady().then(() => tryRefresh('works'));
          return;
        }
        localInFlight.current.works = true;
        dispatch(fetchMyWorks())
          .finally(() => {
            localInFlight.current.works = false;
          })
          .catch(() => {});
        return;
      }

      if (key === 'playlists') {
        if (playlistsFetchInFlight || localInFlight.current.playlists || playlistsStatus === 'loading') return;
        const last = lastFetchedAt.playlists;
        const stale = typeof last !== 'number' || !Number.isFinite(last) || now - last > PLAYLISTS_REFRESH_TTL_MS;
        if (!stale) return;
        localInFlight.current.playlists = true;
        dispatch(fetchMyPlaylists())
          .finally(() => {
            localInFlight.current.playlists = false;
          })
          .catch(() => {});
        return;
      }

      if (key === 'albums') {
        if (
          albumsFetchInFlight ||
          localInFlight.current.albums ||
          albumsStatus === 'loading' ||
          albumsSyncStatus === 'pending'
        )
          return;
        const last = lastFetchedAt.albums;
        const stale = typeof last !== 'number' || !Number.isFinite(last) || now - last > ALBUMS_REFRESH_TTL_MS;
        if (!stale) return;
        localInFlight.current.albums = true;
        dispatch(fetchMyAlbums())
          .finally(() => {
            localInFlight.current.albums = false;
          })
          .catch(() => {});
        return;
      }

      if (key === 'artists') {
        if (artistsFetchInFlight || localInFlight.current.artists || artistsStatus === 'loading') return;
        const last = lastFetchedAt.artists;
        const stale = typeof last !== 'number' || !Number.isFinite(last) || now - last > ARTISTS_REFRESH_TTL_MS;
        if (!stale) return;
        localInFlight.current.artists = true;
        dispatch(fetchMyArtists())
          .finally(() => {
            localInFlight.current.artists = false;
          })
          .catch(() => {});
      }
    },
    [
      dispatch,
      albumsFetchInFlight,
      albumsStatus,
      albumsSyncStatus,
      artistsFetchInFlight,
      artistsStatus,
      lastFetchedAt.albums,
      lastFetchedAt.artists,
      lastFetchedAt.playlists,
      lastFetchedAt.recordings,
      lastFetchedAt.works,
      playlistsFetchInFlight,
      playlistsStatus,
      recordingsFetchInFlight,
      recordingsStatus,
      userId,
      worksFetchInFlight,
      worksStatus,
    ]
  );

  useEffect(() => {
    if (!enabled || disableLegacyFetches) return;
    warmBackend();
    tryRefresh('recordings');
    tryRefresh('works');
    tryRefresh('playlists');
    tryRefresh('albums');
    tryRefresh('artists');
  }, [tryRefresh, warmBackend, enabled, disableLegacyFetches]);

  useEffect(() => {
    if (!enabled || disableLegacyFetches) return;
    const id = window.setInterval(() => {
      warmBackend();
      tryRefresh('recordings');
      tryRefresh('works');
      tryRefresh('playlists');
      tryRefresh('albums');
      tryRefresh('artists');
    }, REFRESH_POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [tryRefresh, warmBackend, enabled, disableLegacyFetches]);
};
